import { BuilderElement, GlobalConfig } from "@/types/builder";
import { generateId } from "@/utils/builder";
import {
  createMentionedInSection,
  createPartnerMenuSection,
  createListingSection,
  createFooterMenuSection,
  createFooterEndSection,
  createFAQSection,
} from "./template2";

// ====================== Header Section ======================
const createHeaderSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    backgroundColor: "background",
    padding: "31px 0",
    width: "100%",
  },
  responsiveStyles: {
    tablet: {
      padding: "20px 0",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        maxWidth: "1496px",
        margin: "0 auto",
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0 50px",
      },
      children: [
        {
          id: generateId(),
          type: "logo" as const,
          styles: {
            maxWidth: "136px",
            display: "flex",
            alignItems: "center",
          },
          responsiveStyles: {
            mobile: {
              maxWidth: "90px",
            },
          },
          children: [
            {
              id: generateId(),
              type: "image" as const,
              src: "/images/movyo/logo.webp",
              alt: "Movyo Logo",
              styles: {
                width: "100%",
                height: "100%",
                objectFit: "contain",
              },
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Contact Hero Section ======================
const createContactHeroSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    padding: "67px 0",
    backgroundImage: "url(/images/movyo/heroBG.webp)",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundSize: "cover",
    width: "100%",
  },
  responsiveStyles: {
    tablet: {
      padding: "30px 0",
    },
    mobile: {
      padding: "20px 0",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        maxWidth: "1496px",
        margin: "0 auto",
        width: "100%",
        padding: "0 50px",
      },
      responsiveStyles: {
        tablet: { padding: "0 20px" },
        mobile: { padding: "0 16px" },
      },
      children: [
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "77px 0",
          },
          responsiveStyles: {
            tablet: {
              gap: "40px 0",
            },
            mobile: {
              gap: "24px 0",
            },
          },
          children: [
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "0",
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "Contact Us",
                  styles: {
                    color: "background",
                    fontFamily: "Inter, sans-serif",
                    fontSize: "58.3px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "100%",
                    paddingBottom: "36px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    tablet: {
                      fontSize: "50px",
                    },
                    mobile: {
                      fontSize: "44px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content: "We're here to help - reach out any time.",
                  styles: {
                    color: "background",
                    fontFamily: "Inter, sans-serif",
                    fontSize: "27.3px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "100%",
                    textAlign: "center",
                    textTransform: "uppercase",
                    margin: "0",
                  },
                  responsiveStyles: {
                    tablet: {
                      fontSize: "20px",
                      lineHeight: "30px",
                    },
                    mobile: {
                      fontSize: "16px",
                      lineHeight: "40px",
                    },
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Contact Us With Section ======================
const createContactUsWithSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    padding: "60px 0",
    backgroundColor: "background",
    width: "100%",
  },
  responsiveStyles: {
    tablet: { padding: "40px 0" },
    mobile: { padding: "30px 0" },
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        maxWidth: "1496px",
        margin: "0 auto",
        width: "100%",
        padding: "0 50px",
      },
      responsiveStyles: {
        tablet: { padding: "0 20px" },
        mobile: { padding: "0 16px" },
      },
      children: [
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: "70px",
            width: "100%",
          },
          responsiveStyles: {
            tablet: {
              gap: "0 35px",
            },
            mobile: {
              flexDirection: "column",
              gap: "20px",
            },
          },
          children: [
            // Email Support Card
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                flex: "0 0 calc(50% - 35px)",
                borderRadius: "16px",
                border: "1px solid #D1D3D4",
                backgroundColor: "background",
                padding: "20px 30px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "45px",
                textAlign: "center",
                height: "428px",
              },
              responsiveStyles: {
                tablet: {
                  padding: "16px",
                  gap: "20px",
                },
                mobile: {
                  flex: "0 0 100%",
                  maxWidth: "400px",
                  height: "auto",
                  gap: "20px",
                  width: "100%",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "12px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as const,
                      src: "/images/movyo/paperplen.svg",
                      alt: "Product Email Support",
                      styles: {
                        width: "68px",
                        height: "auto",
                        objectFit: "contain",
                      },
                      responsiveStyles: {
                        mobile: {
                          width: "68px",
                          height: "auto",
                        },
                      },
                    },
                    {
                      id: generateId(),
                      type: "heading" as const,
                      content: "Product Email Support",
                      styles: {
                        color: "forground",
                        textAlign: "center",
                        fontFamily: "Inter, sans-serif",
                        fontSize: "45px",
                        fontStyle: "normal",
                        fontWeight: "700",
                        lineHeight: "100%",
                        margin: "0",
                      },
                      responsiveStyles: {
                        tablet: {
                          fontSize: "26px",
                          lineHeight: "50px",
                        },
                        mobile: {
                          fontSize: "20px",
                          lineHeight: "40px",
                        },
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "link" as const,
                  href: "mailto:support@movyo.net",
                  content: "support@movyo.net",
                  styles: {
                    color: "link",
                    textAlign: "center",
                    fontFamily: "Inter, sans-serif",
                    fontSize: "26.4px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "100%",
                    textDecoration: "none",
                  },
                  responsiveStyles: {
                    tablet: {
                      fontSize: "20px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content: "*Our team usually responds within a few hours.",
                  styles: {
                    color: "link",
                    textAlign: "center",
                    fontFamily: "Inter, sans-serif",
                    fontSize: "26.4px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "100%",
                    margin: "0",
                  },
                  responsiveStyles: {
                    tablet: {
                      fontSize: "16px",
                      marginTop: "40px",
                    },
                    mobile: {
                      marginTop: "18px",
                    },
                  },
                },
              ],
            },
            // Live Chat Support Card
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                flex: "0 0 calc(50% - 35px)",
                borderRadius: "16px",
                border: "1px solid #D1D3D4",
                backgroundColor: "#FFF",
                padding: "20px 30px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "45px",
                textAlign: "center",
                height: "428px",
              },
              responsiveStyles: {
                tablet: {
                  padding: "16px",
                  gap: "20px",
                },
                mobile: {
                  flex: "0 0 100%",
                  maxWidth: "400px",
                  height: "auto",
                  gap: "20px",
                  width: "100%",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "12px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as const,
                      src: "/images/movyo/chat.svg",
                      alt: "Live Chat Support",
                      styles: {
                        width: "68px",
                        height: "auto",
                        objectFit: "contain",
                      },
                      responsiveStyles: {
                        mobile: {
                          width: "68px",
                          height: "auto",
                        },
                      },
                    },
                    {
                      id: generateId(),
                      type: "heading" as const,
                      content: "Live Chat Support",
                      styles: {
                        color: "forground",
                        textAlign: "center",
                        fontFamily: "Inter, sans-serif",
                        fontSize: "45px",
                        fontStyle: "normal",
                        fontWeight: "700",
                        lineHeight: "100%",
                        margin: "0",
                      },
                      responsiveStyles: {
                        tablet: {
                          fontSize: "26px",
                          lineHeight: "50px",
                        },
                        mobile: {
                          fontSize: "20px",
                          lineHeight: "40px",
                        },
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content: "24/7 Live Chat Support",
                  styles: {
                    color: "#231F20",
                    textAlign: "center",
                    fontFamily: "Inter, sans-serif",
                    fontSize: "26.4px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "100%",
                    textTransform: "uppercase",
                    margin: "0",
                  },
                  responsiveStyles: {
                    tablet: {
                      fontSize: "24px",
                    },
                    mobile: {
                      marginTop: "12px",
                      fontSize: "20px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "button" as const,
                  content: "START LIVE CHAT",
                  styles: {
                    backgroundColor: "#F4B83D",
                    color: "forground",
                    padding: "5px 36px",
                    border: "none",
                    fontSize: "20px",
                    fontWeight: "700",
                    cursor: "pointer",
                    display: "flex",
                    width: "265px",
                    height: "44px",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "24px",
                  },
                  typography: "Button",
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

export const template2Contact = (globalConfig: GlobalConfig) => {
  return {
    header: createHeaderSection(globalConfig) as BuilderElement,
    hero: createContactHeroSection(globalConfig) as BuilderElement,
    contactInfo: createContactUsWithSection(globalConfig) as BuilderElement,
    faq: createFAQSection(globalConfig) as BuilderElement,
    mentioned: createMentionedInSection(globalConfig) as BuilderElement,
    partnerMenu: createPartnerMenuSection(globalConfig) as BuilderElement,
    listing: createListingSection(globalConfig) as BuilderElement,
    footerMenu: createFooterMenuSection(globalConfig) as BuilderElement,
    footerEnd: createFooterEndSection(globalConfig) as BuilderElement,
  };
};

export const template2ContactGlobalConfig = {
  typography: [
    {
      name: "Default",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "400",
      fontStyle: "normal",
    },
    {
      name: "Heading-1",
      fontFamily: "Poppins, sans-serif",
      fontSize: "48px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-2",
      fontFamily: "Poppins, sans-serif",
      fontSize: "36px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-3",
      fontFamily: "Poppins, sans-serif",
      fontSize: "24px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Heading-4",
      fontFamily: "Poppins, sans-serif",
      fontSize: "20px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Heading-5",
      fontFamily: "Poppins, sans-serif",
      fontSize: "18px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Heading-6",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Body",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "400",
      fontStyle: "normal",
    },
    {
      name: "Button",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "600",
      fontStyle: "normal",
    },
  ],
  colors: {
    background: "#FFFFFF",
    forground: "#333333",
    primary: "#667eea",
    secondary: "#764ba2",
    tertiary: "#f8f9fa",
    quaternary: "#e0e0e0",
    link: "#667eea",
    activeLink: "#1D4ED8",
  },
  breakpoints: {
    mobileMax: 640,
    tabletMin: 641,
    tabletMax: 1024,
    desktopMin: 1025,
  },
};
